import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
  fetchPublicConsentFormApi,
  submitPublicConsentApi,
} from "./consentApi";

const baseInputStyle = {
  borderRadius: 10,
  fontSize: 13,
  padding: "10px 12px",
};

const PublicConsentPage = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [publicForm, setPublicForm] = useState(null);
  const [schema, setSchema] = useState(null);

  const [values, setValues] = useState({});
  const [purposeChoices, setPurposeChoices] = useState({});
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [success, setSuccess] = useState(null);
  const [step, setStep] = useState(1);
const [isMinor, setIsMinor] = useState(false);
const [guardianData, setGuardianData] = useState({});
const [consentAction, setConsentAction] = useState("PROVIDE"); 
// PROVIDE or WITHDRAW

const [withdrawalChoices, setWithdrawalChoices] = useState({
  withdrawAll: false,
  withdrawOptional: false,
  withdrawSpecific: false,
});

const [withdrawalReason, setWithdrawalReason] = useState("");
const [withdrawalDeclarationAccepted, setWithdrawalDeclarationAccepted] = useState(false);

  useEffect(() => {
    loadPublicForm();
  }, [token]);

  const loadPublicForm = async () => {
    setLoading(true);
    setError("");

    try {
    const res = await fetchPublicConsentFormApi(token);
const data = res.data;

if (!data) {
  throw new Error("Unable to load consent form");
}

      let parsedSchema = { sections: [], purposes: [] };

try {
  if (typeof data?.schemaJson === "string" && data.schemaJson.trim()) {
    parsedSchema = JSON.parse(data.schemaJson);
  } else if (typeof data?.schemaJson === "object" && data.schemaJson !== null) {
    parsedSchema = data.schemaJson;
  }
} catch (e) {
  console.error("Invalid schemaJson:", data?.schemaJson);
  throw new Error("Consent form schema is invalid");
}

      setPublicForm(data);
      setSchema(parsedSchema);

      const defaultPurposeChoices = {};
      (parsedSchema.purposes || []).forEach((p) => {
        defaultPurposeChoices[p.id] = p.mandatory || !!p.defaultEnabled;
      });
      setPurposeChoices(defaultPurposeChoices);
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to load consent form");
    } finally {
      setLoading(false);
    }
  };

  const dataFields = useMemo(() => {
    const section = schema?.sections?.find((s) => s.key === "DATA_PRINCIPAL");
    return section?.fields || [];
  }, [schema]);

  const updateValue = (fieldId, value) => {
    setValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

 const validateBeforeSubmit = () => {
  if (consentAction === "PROVIDE") {
    for (const field of dataFields) {
      if (field.required && !values[field.id]) {
        return `${field.label} is required`;
      }
    }

    for (const purpose of schema?.purposes || []) {
      if (purpose.mandatory && purposeChoices[purpose.id] !== true) {
        return `${purpose.name} is mandatory`;
      }
    }

    if (!declarationAccepted) {
      return "Please accept the consent declaration";
    }

    if (schema?.childConsent?.enabled && isMinor) {
      if (!guardianData.guardianName) return "Guardian name is required";
      if (!guardianData.guardianContact) return "Guardian contact is required";
      if (!guardianData.relationship) return "Guardian relationship is required";
      if (!guardianData.guardianDeclarationAccepted) {
        return "Guardian declaration must be accepted";
      }
    }
  }

  if (consentAction === "WITHDRAW") {
    const anyWithdrawalSelected =
      withdrawalChoices.withdrawAll ||
      withdrawalChoices.withdrawOptional ||
      withdrawalChoices.withdrawSpecific;

    if (!anyWithdrawalSelected) {
      return "Please select at least one withdrawal option";
    }

    if (!withdrawalDeclarationAccepted) {
      return "Please accept the withdrawal declaration";
    }
  }

  return null;
};

  const submitConsent = async () => {
    const validationError = validateBeforeSubmit();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError("");

   try {
  const payload = {
  formId: publicForm.formId,
  formVersion: publicForm.formVersion,
  noticeVersion: publicForm.noticeVersion,
  values,
  purposeChoices,
  childData: isMinor ? guardianData : null,
  declarationAccepted,
  submittedAt: new Date().toISOString(),
};

  const res = await submitPublicConsentApi(token, payload);
const data = res.data;

if (!data) {
  throw new Error("Consent submission failed");
}

setSuccess(data);
 
} catch (err) {
  console.error(err);
  setError(err?.response?.data?.message || err.message || "Consent submission failed");
} finally {
  setSubmitting(false);
}
  };

  if (loading) {
    return (
      <PublicShell>
        <div className="text-center py-5">
          <Spinner animation="border" />
          <div className="mt-3 text-muted">Loading consent form...</div>
        </div>
      </PublicShell>
    );
  }

  if (error && !publicForm) {
    return (
      <PublicShell>
        <Alert variant="danger">{error}</Alert>
      </PublicShell>
    );
  }

  if (success) {
    return (
      <PublicShell>
        <SuccessScreen response={success} />
      </PublicShell>
    );
  }

return (
  <PublicShell>
    <Card className="mb-3">
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            {schema?.organizationBranding?.logoBase64 && (
              <img
                src={schema.organizationBranding.logoBase64}
                alt="Company Logo"
                style={{ width: 54, height: 54, objectFit: "contain" }}
              />
            )}

            <div>
              <h4 style={{ fontWeight: 800, marginBottom: 4 }}>
                {publicForm?.formName || "Consent Form"}
              </h4>
              <div style={{ fontSize: 13, color: "#6b7280" }}>
                {schema?.organizationBranding?.legalEntityName || "Data Fiduciary"} · {publicForm?.regulation}
              </div>
            </div>
          </div>

          <Badge bg="success">ACTIVE</Badge>
        </div>
      </Card.Body>
    </Card>

    {error && <Alert variant="danger">{error}</Alert>}
    <Card className="mb-3">
  <Card.Header>
    <strong>What would you like to do?</strong>
  </Card.Header>

  <Card.Body>
    <Row>
      <Col md={6}>
        <div
          onClick={() => {
            setConsentAction("PROVIDE");
            setStep(1);
          }}
          style={{
            border: consentAction === "PROVIDE" ? "2px solid #035EA5" : "1px solid #E5E7EB",
            borderRadius: 14,
            padding: 16,
            cursor: "pointer",
            background: consentAction === "PROVIDE" ? "#EFF6FF" : "#FFFFFF",
          }}
        >
          <Form.Check
            type="radio"
            name="consentAction"
            checked={consentAction === "PROVIDE"}
            onChange={() => {
              setConsentAction("PROVIDE");
              setStep(1);
            }}
            label={<strong>Provide Consent</strong>}
          />
          <div style={{ fontSize: 12, color: "#6b7280", marginLeft: 24 }}>
            Give permission for selected data processing purposes.
          </div>
        </div>
      </Col>

      <Col md={6}>
        <div
          onClick={() => {
            setConsentAction("WITHDRAW");
            setStep(1);
          }}
          style={{
            border: consentAction === "WITHDRAW" ? "2px solid #7C3AED" : "1px solid #E5E7EB",
            borderRadius: 14,
            padding: 16,
            cursor: "pointer",
            background: consentAction === "WITHDRAW" ? "#F5F3FF" : "#FFFFFF",
          }}
        >
          <Form.Check
            type="radio"
            name="consentAction"
            checked={consentAction === "WITHDRAW"}
            onChange={() => {
              setConsentAction("WITHDRAW");
              setStep(1);
            }}
            label={<strong>Withdraw Consent</strong>}
          />
          <div style={{ fontSize: 12, color: "#6b7280", marginLeft: 24 }}>
            Withdraw previously provided consent.
          </div>
        </div>
      </Col>
    </Row>
  </Card.Body>
</Card>

    <div className="d-flex justify-content-between mb-3">
 {(
  consentAction === "PROVIDE"
    ? ["Notice", "Data We Collect", "Purposes", "Your Info", "Declaration"]
    : ["Notice", "Withdrawal", "Review"]
).map((x, i) => (
        <div
          key={x}
          style={{
            flex: 1,
            textAlign: "center",
            fontSize: 12,
            fontWeight: step === i + 1 ? 700 : 500,
            color: step === i + 1 ? "#035EA5" : "#6b7280",
          }}
        >
          {i + 1}. {x}
        </div>
      ))}
    </div>

    {step === 1 && (
      <Card className="mb-3">
        <Card.Header>
          <strong>Privacy Notice</strong>
        </Card.Header>
        <Card.Body>
          <p>
            Please read this privacy notice before submitting your consent.
          </p>

          <Row>
            <Col md={6}>
              <strong>Legal Entity</strong>
              <div>{schema?.organizationBranding?.legalEntityName || "-"}</div>
            </Col>
            <Col md={6}>
              <strong>CIN / Registration No.</strong>
              <div>{schema?.organizationBranding?.cin || "-"}</div>
            </Col>
            <Col md={6} className="mt-2">
              <strong>Website</strong>
              <div>{schema?.organizationBranding?.website || "-"}</div>
            </Col>
            <Col md={6} className="mt-2">
              <strong>Contact Email</strong>
              <div>{schema?.organizationBranding?.enquiryEmail || "-"}</div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    )}

    {step === 2 && consentAction === "PROVIDE" && (
      <>
        <Card className="mb-3">
          <Card.Header>
            <strong>Information Collected & Source</strong>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <strong>Data Category</strong>
                <div>{schema?.informationCollected?.dataCategory || "-"}</div>
              </Col>
              <Col md={6}>
                <strong>Source of Collection</strong>
                <div>{schema?.informationCollected?.sourceOfCollection || "-"}</div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header>
            <strong>Legal Basis & Retention</strong>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <strong>Legal Basis</strong>
                <div>{schema?.legalBasis?.basis || "-"}</div>
              </Col>
              <Col md={6}>
                <strong>Retention Period</strong>
                <div>{schema?.retention?.defaultRetentionPeriod || "-"}</div>
              </Col>
            </Row>

            <div className="mt-3" style={{ fontSize: 13, color: "#6b7280" }}>
              {schema?.legalBasis?.description}
            </div>
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header>
            <strong>Third-Party Recipients</strong>
          </Card.Header>
          <Card.Body>
            {schema?.thirdPartyRecipients?.enabled ? (
              <Row>
                <Col md={4}>
                  <strong>Recipient</strong>
                  <div>{schema.thirdPartyRecipients.recipientCategory || "-"}</div>
                </Col>
                <Col md={4}>
                  <strong>Purpose</strong>
                  <div>{schema.thirdPartyRecipients.purpose || "-"}</div>
                </Col>
                <Col md={4}>
                  <strong>Data Shared</strong>
                  <div>{schema.thirdPartyRecipients.dataShared || "-"}</div>
                </Col>
              </Row>
            ) : (
              <Alert variant="success" className="mb-0">
                Your data is collected, stored and managed only within SUSAN. It is not shared with third parties.
              </Alert>
            )}
          </Card.Body>
        </Card>
      </>
    )}

   {step === 3 && consentAction === "PROVIDE" && (
      <Card className="mb-3">
        <Card.Header>
          <strong>Purpose-wise Consent</strong>
        </Card.Header>
        <Card.Body>
          <ConsentPurposeRenderer
            purposes={schema?.purposes || []}
            value={purposeChoices}
            onChange={(purposeId, checked) =>
              setPurposeChoices((prev) => ({
                ...prev,
                [purposeId]: checked,
              }))
            }
          />
        </Card.Body>
      </Card>
    )}

   {step === 4 && consentAction === "PROVIDE" && (
      <>
        <Card className="mb-3">
          <Card.Header>
            <strong>Your Information</strong>
          </Card.Header>
          <Card.Body>
            <Row>
              {dataFields.map((field) => (
                <Col md={6} key={field.id}>
                  <DynamicFieldRenderer
                    field={field}
                    value={values[field.id]}
                    onChange={(value) => updateValue(field.id, value)}
                  />
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>

        {schema?.childConsent?.enabled && (
          <Card className="mb-3" style={{ border: "1px solid #f59e0b" }}>
            <Card.Header>
              <Form.Check
                type="checkbox"
                checked={isMinor}
                onChange={(e) => setIsMinor(e.target.checked)}
                label="This consent involves personal data of a child below 18 years"
              />
            </Card.Header>

            {isMinor && (
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{schema.childConsent.guardianNameLabel}</Form.Label>
                      <Form.Control
                        value={guardianData.guardianName || ""}
                        onChange={(e) =>
                          setGuardianData({ ...guardianData, guardianName: e.target.value })
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{schema.childConsent.guardianContactLabel}</Form.Label>
                      <Form.Control
                        value={guardianData.guardianContact || ""}
                        onChange={(e) =>
                          setGuardianData({ ...guardianData, guardianContact: e.target.value })
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Relationship</Form.Label>
                      <Form.Select
                        value={guardianData.relationship || ""}
                        onChange={(e) =>
                          setGuardianData({ ...guardianData, relationship: e.target.value })
                        }
                      >
                        <option value="">Select...</option>
                        {(schema.childConsent.relationshipOptions || []).map((x) => (
                          <option key={x} value={x}>{x}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Check
                  type="checkbox"
                  checked={guardianData.guardianDeclarationAccepted === true}
                  onChange={(e) =>
                    setGuardianData({
                      ...guardianData,
                      guardianDeclarationAccepted: e.target.checked,
                    })
                  }
                  label={schema.childConsent.guardianDeclarationText}
                />
              </Card.Body>
            )}
          </Card>
        )}
      </>
    )}

   {step === 5 && consentAction === "PROVIDE" && (
      <Card className="mb-3">
        <Card.Header>
          <strong>Review & Declaration</strong>
        </Card.Header>
        <Card.Body>
          <Alert variant="info">
            Please confirm that all information is correct before submitting.
          </Alert>

          <Form.Check
            type="checkbox"
            checked={declarationAccepted}
            onChange={(e) => setDeclarationAccepted(e.target.checked)}
            label={
              schema?.consentDeclaration?.declarationText ||
              "I confirm that I have read and understood the Privacy Notice and voluntarily provide my consent."
            }
          />

          {schema?.withdrawal?.enabled && (
            <div className="mt-3" style={{ fontSize: 13, color: "#6b7280" }}>
              {schema.withdrawal.withdrawalText}
            </div>
          )}
        </Card.Body>
      </Card>
    )}
{step === 2 && consentAction === "WITHDRAW" && (
  <Card className="mb-3">
    <Card.Header>
      <strong>Consent Withdrawal Options</strong>
    </Card.Header>

    <Card.Body>
      <Alert variant="info">
        {schema?.withdrawal?.withdrawalText ||
          "You may withdraw consent at any time. Prior lawful processing remains valid after withdrawal."}
      </Alert>

      {schema?.withdrawal?.allowWithdrawAll && (
        <Form.Check
          className="mb-3"
          type="checkbox"
          label="Withdraw All Consent"
          checked={withdrawalChoices.withdrawAll}
          onChange={(e) =>
            setWithdrawalChoices({
              ...withdrawalChoices,
              withdrawAll: e.target.checked,
            })
          }
        />
      )}

      {schema?.withdrawal?.allowWithdrawOptional && (
        <Form.Check
          className="mb-3"
          type="checkbox"
          label="Withdraw Optional / Marketing Consent"
          checked={withdrawalChoices.withdrawOptional}
          onChange={(e) =>
            setWithdrawalChoices({
              ...withdrawalChoices,
              withdrawOptional: e.target.checked,
            })
          }
        />
      )}

      {schema?.withdrawal?.allowWithdrawSpecific && (
        <Form.Check
          className="mb-3"
          type="checkbox"
          label="Withdraw Specific Purpose Consent"
          checked={withdrawalChoices.withdrawSpecific}
          onChange={(e) =>
            setWithdrawalChoices({
              ...withdrawalChoices,
              withdrawSpecific: e.target.checked,
            })
          }
        />
      )}

      <Form.Group className="mb-3">
        <Form.Label>Reason for Withdrawal Optional</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={withdrawalReason}
          onChange={(e) => setWithdrawalReason(e.target.value)}
          placeholder="You may provide a reason for withdrawal"
        />
      </Form.Group>
    </Card.Body>
  </Card>
)}

{step === 3 && consentAction === "WITHDRAW" && (
  <Card className="mb-3">
    <Card.Header>
      <strong>Review Withdrawal Declaration</strong>
    </Card.Header>

    <Card.Body>
      <Alert variant="warning">
        Please confirm that you want to withdraw the selected consent permissions.
      </Alert>

      <Form.Check
        type="checkbox"
        checked={withdrawalDeclarationAccepted}
        onChange={(e) => setWithdrawalDeclarationAccepted(e.target.checked)}
        label="I confirm that I want to withdraw my consent as selected above."
      />
    </Card.Body>
  </Card>
)}
    <div className="d-flex justify-content-between">
      <Button
        variant="outline-secondary"
        disabled={step === 1}
        onClick={() => setStep((prev) => prev - 1)}
      >
        Back
      </Button>

      {step < (consentAction === "PROVIDE" ? 5 : 3) ? (
  <Button onClick={() => setStep((prev) => prev + 1)}>
    Next
  </Button>
) : (
  <Button onClick={submitConsent} disabled={submitting}>
    {submitting
      ? "Submitting..."
      : consentAction === "PROVIDE"
        ? "I Agree & Submit"
        : "Submit Withdrawal"}
  </Button>
)}
    </div>
  </PublicShell>
);
};

const PublicShell = ({ children }) => {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#F8FAFC",
        padding: "32px 16px",
      }}
    >
      <div style={{ maxWidth: 980, margin: "0 auto" }}>
        <div className="mb-3">
          <h5 style={{ fontWeight: 800, marginBottom: 0 }}>SUSAN Consent</h5>
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            Privacy · Trust · Governance
          </div>
        </div>
        {children}
      </div>
    </div>
  );
};

const DynamicFieldRenderer = ({ field, value, onChange }) => {
  const commonProps = {
    value: value || "",
    onChange: (e) => onChange(e.target.value),
    placeholder: field.placeholder || "",
    style: baseInputStyle,
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label style={{ fontWeight: 600, fontSize: 13 }}>
        {field.label}
        {field.required && <span className="text-danger ms-1">*</span>}
        {field.sensitive && (
          <Badge bg="warning" className="ms-2">
            Sensitive
          </Badge>
        )}
      </Form.Label>

      {field.type === "textarea" ? (
        <Form.Control as="textarea" rows={3} {...commonProps} />
      ) : field.type === "dropdown" ? (
        <Form.Select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          style={baseInputStyle}
        >
          <option value="">Select...</option>
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </Form.Select>
      ) : field.type === "radio" ? (
        <div>
          {(field.options || []).map((opt) => (
            <Form.Check
              key={opt}
              type="radio"
              name={field.id}
              label={opt}
              checked={value === opt}
              onChange={() => onChange(opt)}
            />
          ))}
        </div>
      ) : field.type === "checkbox" ? (
        <Form.Check
          type="checkbox"
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
          label={field.placeholder || "Yes"}
        />
      ) : (
        <Form.Control type={field.type || "text"} {...commonProps} />
      )}
    </Form.Group>
  );
};

const ConsentPurposeRenderer = ({ purposes, value, onChange }) => {
  return (
    <div>
      {purposes.map((purpose) => {
        const checked = purpose.mandatory ? true : value[purpose.id] === true;

        return (
          <div
            key={purpose.id}
            style={{
              border: checked ? "1.5px solid #008080" : "1px solid #E2E8F0",
              background: checked ? "#F0FDFD" : "#FFFFFF",
              borderRadius: 12,
              padding: 14,
              marginBottom: 10,
            }}
          >
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>
                  {purpose.name}
                  <Badge
                    bg={purpose.mandatory ? "success" : "secondary"}
                    className="ms-2"
                  >
                    {purpose.mandatory ? "Mandatory" : "Optional"}
                  </Badge>
                </div>
                {purpose.description && (
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                    {purpose.description}
                  </div>
                )}
              </div>

              <Form.Check
                type="switch"
                checked={checked}
                disabled={purpose.mandatory}
                onChange={(e) => onChange(purpose.id, e.target.checked)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const SuccessScreen = ({ response }) => {
  return (
    <Card>
      <Card.Body className="text-center py-5">
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            background: "#DCFCE7",
            color: "#166534",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 36,
            marginBottom: 16,
          }}
        >
          ✓
        </div>

        <h4 style={{ fontWeight: 800 }}>Request Submitted</h4>
<p className="text-muted">Your consent request has been submitted successfully.</p>

        <Card style={{ maxWidth: 420, margin: "20px auto" }}>
          <Card.Body>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Consent ID</div>
            <div style={{ fontWeight: 800 }}>{response?.consentId}</div>
          </Card.Body>
        </Card>

        <Button onClick={() => window.close()} variant="outline-primary">
          Done
        </Button>
      </Card.Body>
    </Card>
  );
};

export default PublicConsentPage;